<template>
	<div class="sheet-list">
		<!-- Empty state -->
		<div v-if="!loading && sheets.length === 0" class="empty-state">
			<div class="empty-state-icon">
				<v-icon size="32" color="primary">mdi-file-document-outline</v-icon>
			</div>
			<div v-test="'sheets-empty-headline'" class="text-h6 font-weight-bold mt-3 text-center">No sheets yet</div>
			<div v-test="'sheets-empty-subheadline'" class="text-body-2 text-grey mt-2 mb-4 text-center">
				Send your customer a summary of this project — photos, video, notes, contact information — by text or email.
			</div>
			<v-btn
				v-if="!isViewOnly"
				v-test="'sheets-empty-create-btn'"
				color="primary"
				variant="flat"
				rounded="xl"
				size="large"
				prepend-icon="mdi-plus"
				:loading="creating"
				@click="handleCreate">
				Create Sheet
			</v-btn>
		</div>

		<!-- Loading -->
		<div v-else-if="loading" class="d-flex justify-center pa-6">
			<v-progress-circular indeterminate color="primary" size="32" />
		</div>

		<!-- Sheet list -->
		<template v-else>
			<div class="d-flex align-center justify-space-between mb-3">
				<span class="text-body-2 text-grey">{{ sheets.length }} sheet{{ sheets.length !== 1 ? "s" : "" }}</span>
				<v-btn
					v-if="!isViewOnly"
					color="primary"
					variant="flat"
					rounded="xl"
					size="small"
					prepend-icon="mdi-plus"
					:loading="creating"
					@click="handleCreate">
					New Sheet
				</v-btn>
			</div>

			<div class="sheets-container">
				<div
					v-for="sheet in sheets"
					:key="sheet.id"
					class="sheet-row"
					@click="isViewOnly ? $emit('viewSheet', sheet) : $emit('editSheet', sheet)">
					<div class="d-flex align-center ga-3 flex-grow-1" style="min-width: 0">
						<v-badge
							:model-value="getMediaCount(sheet) > 0"
							:content="getMediaCount(sheet)"
							color="primary"
							offset-x="2"
							offset-y="2"
							class="flex-shrink-0">
							<v-avatar color="accent" size="36">
								<v-icon color="primary" size="18">mdi-file-document-outline</v-icon>
							</v-avatar>
						</v-badge>
						<div class="flex-grow-1" style="min-width: 0">
							<div class="text-body-2 font-weight-medium text-truncate">
								{{ sheet.name || "Untitled Sheet" }}
							</div>
							<div class="d-flex align-center text-caption text-grey ga-1 flex-nowrap">
								<span class="flex-shrink-0">{{ formatDate(sheet.createdAt) }}</span>
								<span v-if="getCreatorName(sheet)" class="text-truncate">&middot; {{ getCreatorName(sheet) }}</span>
							</div>
						</div>
					</div>
					<div class="d-flex align-center ga-1 flex-shrink-0">
						<div class="sheet-actions d-flex ga-1 flex-shrink-0">
							<v-tooltip v-if="!isViewOnly" location="top">
								<template #activator="{ props: tp }">
									<v-btn
										v-bind="tp"
										icon="icon-pencil-edit-stroke"
										aria-label="Edit sheet"
										variant="text"
										density="compact"
										@click.stop="$emit('editSheet', sheet)" />
								</template>
								<span>Edit</span>
							</v-tooltip>
							<v-tooltip v-if="isViewOnly" location="top">
								<template #activator="{ props: tp }">
									<v-btn
										v-bind="tp"
										icon="icon-eye-stroke"
										aria-label="View sheet"
										variant="text"
										density="compact"
										@click.stop="$emit('viewSheet', sheet)" />
								</template>
								<span>View</span>
							</v-tooltip>
							<v-tooltip v-if="canDelete(sheet)" location="top">
								<template #activator="{ props: tp }">
									<v-btn
										v-bind="tp"
										icon="icon-trash-full-stroke"
										aria-label="Delete sheet"
										variant="text"
										density="compact"
										color="error"
										@click.stop="handleDelete(sheet)" />
								</template>
								<span>Delete</span>
							</v-tooltip>
						</div>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script lang="ts" setup>
	import { ref, onMounted, getCurrentInstance } from "vue";
	import { useToast } from "vue-toastification";
	import api from "@/api";
	import { formatDate } from "@/helpers/formatDate";
	import { couldNot } from "@/helpers/userMessages";

	interface Sheet {
		id: string;
		accessToken: string;
		name: string | null;
		subtitle: string | null;
		notes: string | null;
		showDetails: boolean;
		showNotes: boolean;
		showGallery: boolean;
		includeDetails: boolean;
		includeCaptions: boolean;
		createdAt: string;
		sheetUrl?: string;
		Creator?: {
			id: string;
			firstname: string;
			lastname: string;
			email: string;
		} | null;
		GalleryMedia?: { id: string; mediaId: string; mediaType: string }[];
	}

	interface Props {
		projectId: string;
		isAdmin: boolean;
		isViewOnly: boolean;
		isAuthenticated: boolean;
		hasFreeTrialEnded: boolean;
		me?: any;
	}

	const props = defineProps<Props>();

	const emit = defineEmits<{
		(e: "editSheet", sheet: Sheet): void;
		(e: "viewSheet", sheet: Sheet): void;
		(e: "sheetCreated", sheet: Sheet): void;
	}>();

	const instance = getCurrentInstance();
	const toast = useToast();
	const sheets = ref<Sheet[]>([]);
	const loading = ref(true);
	const creating = ref(false);

	onMounted(async () => {
		await loadSheets();
	});

	async function loadSheets() {
		if (!props.projectId) {
			loading.value = false;
			return;
		}
		loading.value = true;
		try {
			const result = await api.getSheetsForProject(props.projectId);
			sheets.value = result ?? [];
		} catch (err) {
			console.error("Failed to load sheets", err);
			toast.error(couldNot("load your sheets"));
		} finally {
			loading.value = false;
		}
	}

	async function handleCreate() {
		creating.value = true;
		try {
			const result = await api.createSheet({ projectId: props.projectId });
			sheets.value.unshift(result);
			emit("sheetCreated", result);
		} catch (err) {
			toast.error(couldNot("create the sheet"));
		} finally {
			creating.value = false;
		}
	}

	function getMediaCount(sheet: Sheet): number {
		return sheet.GalleryMedia?.length ?? 0;
	}

	function getCreatorName(sheet: Sheet): string {
		if (!sheet.Creator) return "";
		return `${sheet.Creator.firstname || ""} ${sheet.Creator.lastname || ""}`.trim();
	}

	async function handleDelete(sheet: Sheet) {
		const confirmed = await (instance?.proxy as any)?.$root?.$confirm?.open(
			"Delete Sheet",
			"This will permanently delete this sheet. This can't be undone.",
			true,
			"Delete",
			"Cancel",
			false
		);
		if (!confirmed) return;

		try {
			await api.deleteSheet(sheet.id);
			sheets.value = sheets.value.filter((s) => s.id !== sheet.id);
			toast.success("The sheet has been removed.");
		} catch (err) {
			toast.error(couldNot("remove the sheet"));
		}
	}

	function canDelete(sheet: Sheet): boolean {
		if (props.isViewOnly) return false;
		if (props.isAdmin) return true;
		if (props.me && sheet.Creator && sheet.Creator.id === props.me?.user?.id) return true;
		return false;
	}

	defineExpose({ loadSheets });
</script>

<style scoped>
	.sheet-list {
		padding: 0;
	}

	/* Empty state */
	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		padding: 40px 24px;
	}

	.empty-state-icon {
		width: 56px;
		height: 56px;
		border-radius: 50%;
		background: rgb(var(--v-theme-accent));
		display: flex;
		align-items: center;
		justify-content: center;
	}

	/* Sheets container */
	.sheets-container {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.sheet-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 10px 14px;
		cursor: pointer;
		border: 1px solid #e8e8e8;
		border-radius: 10px;
		transition:
			background-color 0.12s ease,
			border-color 0.12s ease;
	}

	.sheet-row:hover {
		background-color: #f8f9fb;
		border-color: #d0d0d0;
	}
</style>
